import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Loader2, X as CloseIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import yLogoDark from "@/assets/brand/y-logo-dark.png";
import { AppleLogo, GoogleLogo } from "@/components/BrandLogos";
import { useGoogleSignIn } from "@/lib/googleAuth";
import { useAppleSignIn } from "@/lib/appleAuth";
import { useAuth } from "@/context/AuthContext";
import { apiError as fmtApiError } from "@/lib/api";
import { Seo } from "@/lib/useSeo";

// ---- small brand logos are their own component so the mark rendering
// stays sharp on any theme, and other pages can reuse them. ----

export default function Landing() {
  const navigate = useNavigate();
  const { googleWebLogin, appleWebLogin } = useAuth();
  const [emailChoice, setEmailChoice] = useState(false);
  // Per-button busy flag so tapping Apple doesn't also grey out Google/Email.
  const [ssoBusy, setSsoBusy] = useState(null); // "apple" | "google" | null
  // Persistent in-content error banner.  Toasts vanish after a few
  // seconds and are easy to miss on tall / dark pages; the banner
  // stays visible until the user retries or dismisses it, and shows
  // the raw Xano message so it's obvious what went wrong.
  const [authError, setAuthError] = useState(null); // { provider, message }

  // AuthContext.googleWebLogin / appleWebLogin now commit `authed` + welcome
  // splash atomically after fetching onboarding status, so route guards
  // never see a stale state.  Callers here just await, surface errors on
  // failure, and let <WelcomeGate/> own the transition.

  const surfaceError = (provider, message) => {
    setAuthError({ provider, message });
    // Keep the toast too — some users notice it faster than the banner.
    toast.error(message);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const { ready: googleReady, error: googleError, signIn: googleSignIn, renderRef } =
    useGoogleSignIn({
      onCredential: async (idToken) => {
        try {
          setSsoBusy("google");
          setAuthError(null);
          await googleWebLogin(idToken);
          // AuthContext committed welcome + authed; WelcomeGate takes over.
        } catch (err) {
          surfaceError("google", fmtApiError(err) || "Google sign-in failed. Please try again.");
        } finally {
          setSsoBusy(null);
        }
      },
      onError: (err) => {
        surfaceError("google", err?.message || "Google Sign-In failed to load.");
      },
    });

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const { ready: appleReady, error: appleError, signIn: appleSignIn } = useAppleSignIn();

  const goSocial = async (kind) => {
    if (kind === "google") {
      if (!googleReady) {
        toast.info("Google Sign-In is loading. Please try again in a moment.");
        return;
      }
      if (googleError) {
        surfaceError("google", "Google Sign-In failed to load. Check your connection.");
        return;
      }
      setSsoBusy("google");
      setAuthError(null);
      googleSignIn();
      setTimeout(() => setSsoBusy((s) => (s === "google" ? null : s)), 1200);
      return;
    }
    if (kind === "apple") {
      if (!appleReady) {
        toast.info("Sign in with Apple is loading. Please try again in a moment.");
        return;
      }
      if (appleError) {
        surfaceError("apple", "Apple Sign-In failed to load. Check your connection.");
        return;
      }
      try {
        setSsoBusy("apple");
        setAuthError(null);
        const { code, identityToken } = await appleSignIn();
        await appleWebLogin(code, identityToken);
        // AuthContext committed welcome + authed; WelcomeGate takes over.
      } catch (err) {
        // Structured Apple errors from useAppleSignIn: err.appleErrorCode
        const ac = err?.appleErrorCode;
        if (ac === "cancelled" || ac === "in_flight") {
          /* silent — user closed the popup or double-clicked */
        } else if (ac === "no_code" || ac === "no_identity_token" || ac === "state_mismatch") {
          surfaceError("apple", err.message);
        } else if (ac === "apple_never_responded") {
          surfaceError("apple", err.message);
        } else if (ac === "invalid_client") {
          surfaceError("apple", err.message);
        } else if (ac === "sdk_not_ready" || ac === "sdk_error" || ac === "init_failed" || ac === "popup_blocked") {
          surfaceError("apple", err.message);
        } else {
          // Backend (Xano) error or unclassified failure — the message
          // typically reads "Invalid Apple authorization code" or a
          // network error.  These are the ones the user MUST see
          // clearly in the content area, per the recent bug report.
          surfaceError("apple", fmtApiError(err) || "Sign in with Apple didn't complete. Please try again.");
        }
      } finally {
        setSsoBusy(null);
      }
      return;
    }
  };

  return (
    <div
      className="min-h-dvh w-full bg-black text-white flex flex-col"
      data-testid="landing-screen"
      style={{
        fontFamily:
          "'Plus Jakarta Sans','TwitterChirp','Segoe UI',Helvetica,Arial,sans-serif",
      }}
    >
      <Seo
        title="BrontoY — Share Moments. Express Views."
        description="Join BrontoY — a social platform by Bronto Intelligence for sharing moments, expressing views, and starting real conversations. Sign in with Apple, Google, or email."
        canonicalPath="/"
      />
      {/* MAIN — mobile: logo on top; desktop: logo on right */}
      <main className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 px-6 py-10 lg:py-16 max-w-[1400px] w-full mx-auto">
        {/* Logo — top on mobile, right on desktop.  order-1 lg:order-2 */}
        <div className="order-1 lg:order-2 flex-shrink-0 flex items-center justify-center w-full lg:w-auto">
          <img
            src={yLogoDark}
            alt="BrontoY"
            className="h-40 md:h-56 lg:h-[480px] w-auto select-none drop-shadow-[0_0_60px_rgba(200,120,255,0.35)]"
            draggable={false}
            data-testid="landing-y-logo"
          />
        </div>

        {/* Left side — headline + buttons */}
        <div className="order-2 lg:order-1 w-full max-w-[440px] flex flex-col">
          <h1
            className="text-[36px] leading-[1.05] md:text-[54px] lg:text-[64px] font-extrabold tracking-tight text-center lg:text-left"
            data-testid="landing-headline"
          >
            Share Moments.
            <br />
            Express Views.
          </h1>

          {/* Auth buttons */}
          <div className="mt-9 flex flex-col gap-3">
            {/* In-content error banner — stays visible until the user
                retries or dismisses.  Shows the raw Xano/Apple message
                so it's obvious what went wrong (e.g. "Invalid Apple
                authorization code" from /auth/apple/web returning a
                Throw-Error envelope on HTTP 200). */}
            {authError && (
              <div
                role="alert"
                className="flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 text-red-100 px-4 py-3"
                data-testid="landing-auth-error"
              >
                <AlertTriangle
                  size={18}
                  className="mt-0.5 shrink-0 text-red-300"
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-red-200 mb-0.5">
                    {authError.provider === "apple"
                      ? "Sign in with Apple failed"
                      : authError.provider === "google"
                      ? "Sign in with Google failed"
                      : "Sign in failed"}
                  </div>
                  <div className="text-[12.5px] leading-[17px] break-words" data-testid="landing-auth-error-message">
                    {authError.message}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAuthError(null)}
                  className="shrink-0 rounded-full p-1 text-red-200 hover:bg-red-500/20"
                  aria-label="Dismiss error"
                  data-testid="landing-auth-error-dismiss"
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            )}
            {!emailChoice && (
              <>
                <SsoButton
                  onClick={() => goSocial("apple")}
                  disabled={ssoBusy === "apple"}
                  testId="landing-continue-apple"
                  Icon={AppleLogo}
                  label="Continue with Apple"
                />
                <SsoButton
                  onClick={() => goSocial("google")}
                  disabled={ssoBusy === "google"}
                  testId="landing-continue-google"
                  Icon={GoogleLogo}
                  label="Continue with Google"
                />
                <SsoButton
                  onClick={() => setEmailChoice(true)}
                  testId="landing-continue-email"
                  Icon={MailIcon}
                  label="Continue with Email"
                />
              </>
            )}

            {emailChoice && (
              <>
                <SsoButton
                  onClick={() => navigate("/login")}
                  testId="landing-choice-login"
                  label="Log In"
                />
                <SsoButton
                  onClick={() => navigate("/signup")}
                  testId="landing-choice-signup"
                  label="Sign Up"
                />
                <button
                  type="button"
                  onClick={() => setEmailChoice(false)}
                  className="text-white/60 hover:text-white text-[13px] font-medium mt-1 self-center"
                  data-testid="landing-choice-back"
                >
                  ← Back
                </button>
              </>
            )}
          </div>

          {/* Small fine print (only when SSO/Continue-with-email menu shows) */}
          {!emailChoice && (
            <p className="mt-5 text-white/55 text-[12.5px] leading-[17px] max-w-[400px] text-center lg:text-left">
              By continuing, you agree to our{" "}
              <button
                type="button"
                onClick={() => navigate("/terms")}
                className="underline hover:text-white"
              >
                Terms
              </button>
              ,{" "}
              <button
                type="button"
                onClick={() => navigate("/privacy")}
                className="underline hover:text-white"
              >
                Privacy Policy
              </button>{" "}
              and{" "}
              <button
                type="button"
                onClick={() => navigate("/guidelines")}
                className="underline hover:text-white"
              >
                Community Guidelines
              </button>
              .
            </p>
          )}
        </div>
      </main>

      {/* FOOTER — dark-mode Y logomark + BrontoY - By Bronto Intelligence + link row */}
      <footer className="w-full pt-8 pb-8 flex flex-col items-center gap-3 border-t border-white/10">
        <img
          src={yLogoDark}
          alt="BrontoY"
          className="h-8 w-auto opacity-95"
          draggable={false}
          data-testid="landing-footer-logo"
        />
        <p className="text-white/70 text-[13px] font-medium">
          BrontoY <span className="text-white/40">·</span> By Bronto
          Intelligence
        </p>
        <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-white/55 text-[12.5px]">
          <FooterLink to="/terms" testId="landing-footer-terms">
            Terms
          </FooterLink>
          <FooterLink to="/privacy" testId="landing-footer-privacy">
            Privacy
          </FooterLink>
          <FooterLink to="/guidelines" testId="landing-footer-guidelines">
            Community Guidelines
          </FooterLink>
          <FooterLink to="/support" testId="landing-footer-support">
            Support
          </FooterLink>
          <FooterLink to="/help" testId="landing-footer-help">
            Help
          </FooterLink>
          <FooterLink to="/contact" testId="landing-footer-contact">
            Contact
          </FooterLink>
          <span className="text-white/55">© 2026 BrontoY</span>
        </nav>
      </footer>

      {/* Hidden Google button — GSI renders its real button in here so
          clicking our visible custom "Continue with Google" pill can
          forward the click through to it (opens the account-chooser
          popup and delivers an ID token via the callback we register
          in useGoogleSignIn).  Positioned off-screen but still mounted
          so GSI can layout it. */}
      <div
        ref={renderRef}
        aria-hidden="true"
        data-testid="landing-google-hidden-btn"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      />
    </div>
  );
}

// -------- helpers --------
function MailIcon({ size = 18 }) {
  return <Mail size={size} strokeWidth={2.2} className="text-black" />;
}

function SsoButton({ onClick, disabled, testId, Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full h-[46px] rounded-full bg-white text-black font-bold text-[15px] flex items-center justify-center gap-2.5 hover:opacity-90 active:opacity-80 disabled:opacity-60 transition-opacity"
      data-testid={testId}
    >
      {disabled ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : Icon ? (
        <Icon size={18} />
      ) : null}
      <span>{label}</span>
    </button>
  );
}

function FooterLink({ to, testId, children }) {
  const nav = useNavigate();
  return (
    <button
      type="button"
      onClick={() => nav(to)}
      className="hover:text-white underline-offset-4 hover:underline"
      data-testid={testId}
    >
      {children}
    </button>
  );
}
